import React, { useState, useEffect, useCallback } from 'react';
import { BACKEND_URL, isOpenPayEnabled } from '../config';
import { useLanguage } from '../contexts/LanguageContext';
import { useModalClose } from '../hooks/useModalClose';
import KeyboardAwareNumberInput from './Shared/KeyboardAwareNumberInput';
import { openCashDrawer } from '../utils/receiptPrinter';
import { useSystemFeatures } from '../hooks/useSystemFeatures';
import { formatTaxRate } from '../utils/systemFeatures';
import { PAYMENT_METHODS, CASH_PAYMENT_TYPE_ID, CARD_PAYMENT_TYPE_ID, paymentMethodKey, tenderTipTotal } from '../utils/paymentMethods';

// The screen decides in whole cents, the way the backend totals in whole cents,
// so "does this tender cover the balance" never turns on whether 0.1 + 0.2
// reached 0.3 in a float.
const toCents = (value) => Math.round(Number(value || 0) * 100);

function OrderClose({ orderId, orderItems, totalPrice, tableName, orderName, theme, user, onClose, onOrderClosed }) {
    const { t } = useLanguage();
    const { serviceChargeEnabled, splitPaymentsEnabled, taxRate } = useSystemFeatures();
    useModalClose(onClose);
    const [paymentTypeId, setPaymentTypeId] = useState(null); // No default selection
    const [tipAmount, setTipAmount] = useState(0);
    const [serviceCharge, setServiceCharge] = useState(0);
    const [cashReceived, setCashReceived] = useState('');
    const [heardAboutId, setHeardAboutId] = useState(1); // Default to "Other / Not Specified"
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    // eslint-disable-next-line no-unused-vars
    const [applicableSpecials, setApplicableSpecials] = useState([]);
    // eslint-disable-next-line no-unused-vars
    const [checkingSpecials, setCheckingSpecials] = useState(true);
    const [selectedSpecial, setSelectedSpecial] = useState(null);
    // eslint-disable-next-line no-unused-vars
    const [specialDiscount, setSpecialDiscount] = useState(0);
    const [availableRegisters, setAvailableRegisters] = useState([]);
    // eslint-disable-next-line no-unused-vars
    const [selectedRegisterId, setSelectedRegisterId] = useState(null);
    const [showRegisterSelector, setShowRegisterSelector] = useState(false);
    // Which call asked for a register, so the retry runs that one again
    const [pendingRegisterAction, setPendingRegisterAction] = useState(null);

    // Split payment state. The tenders already recorded against this order and
    // what the server says is still owed on it.
    const [recordedTenders, setRecordedTenders] = useState([]);
    const [outstanding, setOutstanding] = useState(null);
    const [tenderAmount, setTenderAmount] = useState('');
    const [recordingTender, setRecordingTender] = useState(false);
    const [tenderNotice, setTenderNotice] = useState('');

    // OpenPay card payment state
    const openPayEnabled = isOpenPayEnabled();
    const [cardNumber, setCardNumber] = useState('');
    const [cardholderName, setCardholderName] = useState('');
    const [expirationMonth, setExpirationMonth] = useState('');
    const [expirationYear, setExpirationYear] = useState('');
    const [cvv, setCvv] = useState('');
    const [processingCard, setProcessingCard] = useState(false);
    const [cardProcessingStep, setCardProcessingStep] = useState(''); // 'tokenizing' or 'charging'
    const [cardPaymentResult, setCardPaymentResult] = useState(null); // { success, charge_id, authorization } or { error }
    const [showNoTipConfirm, setShowNoTipConfirm] = useState(false);

    // Calculate subtotal (before discounts and IVA) and discount amount
    const subtotalBeforeDiscountAndIVA = orderItems.reduce((sum, item) => {
        return sum + (item.price * item.quantity);
    }, 0);

    // Calculate discount amount - discount is stored as per-unit value, multiply by quantity
    const computedDiscount = orderItems.reduce((sum, item) => {
        return sum + (Number(item.discount || 0) * item.quantity);
    }, 0);

    // Calculate subtotal after discount but before IVA
    const subtotalAfterDiscount = subtotalBeforeDiscountAndIVA - computedDiscount;

    // Calculate IVA on the discounted amount, at the tenant's configured rate
    const ivaAmount = subtotalAfterDiscount * (Number(taxRate) / 100);

    // Final total is the total from Order Summary plus any service charge and tip
    // (totalPrice already has discounts applied at the item level)
    const finalTotal = totalPrice + Number(serviceCharge || 0) + Number(tipAmount);

    // Check if order is zero-balance (free/complimentary)
    const isZeroBalance = finalTotal === 0;

    // More than one tender is offered only where the tenant has the feature on,
    // and never on a zero-balance check, which has nothing to split.
    const splitActive = splitPaymentsEnabled && !isZeroBalance;

    // The tips already banked on earlier tenders. orders.tip is the whole order's
    // tip, so the close has to send this plus whatever the last tender adds.
    const recordedTipTotal = tenderTipTotal(recordedTenders);
    const recordedAmountTotal = recordedTenders.reduce((sum, row) => sum + (Number(row.amount) || 0), 0);

    const outstandingValue = outstanding === null ? null : Number(outstanding);
    const tenderCents = toCents(tenderAmount);
    // With the feature off there is one tender for the whole bill, which is what
    // the Close Check button has always sent.
    const tenderCoversRemainder = !splitActive || outstandingValue === null || tenderCents >= toCents(outstandingValue);
    // The amount this tender settles, which is the whole bill when it is the
    // only one.
    const tenderTotal = splitActive ? Number(tenderAmount || 0) + Number(tipAmount || 0) : finalTotal;

    // Calculate change for cash payments, on the cash leg rather than on the
    // whole bill: a cashier taking 400 in cash against a 1000 check is owed
    // change on the 400.
    const cashChange = paymentTypeId === CASH_PAYMENT_TYPE_ID && cashReceived ? Number(cashReceived) - tenderTotal : 0;

    // The one action button either records a tender and leaves the check open,
    // or closes the check, which is what it has always done. Which one it is
    // depends on whether this tender covers what is left.
    const busy = loading || recordingTender;
    const recordsTender = splitActive && !tenderCoversRemainder;
    const actionDisabled = busy
        || (!isZeroBalance && !paymentTypeId)
        || (openPayEnabled && paymentTypeId === CARD_PAYMENT_TYPE_ID && !isZeroBalance && !cardPaymentResult?.success)
        || (splitActive && tenderCents <= 0);

    // Check for applicable specials when component mounts
    useEffect(() => {
        checkApplicableSpecials();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // The tenders already recorded and what is still owed. outstanding comes
    // from the server rather than from a subtraction here, so the screen and the
    // ledger can never drift apart by a centavo.
    const loadTenders = useCallback(async () => {
        if (!orderId || !splitPaymentsEnabled) return null;

        try {
            const response = await fetch(`${BACKEND_URL}/api/orders/${orderId}/payments`);
            if (!response.ok) return null;
            const data = await response.json();
            setRecordedTenders(data.payments || []);
            setOutstanding(data.outstanding);
            return data;
        } catch (err) {
            console.error('Error loading recorded payments:', err);
            return null;
        }
    }, [orderId, splitPaymentsEnabled]);

    useEffect(() => {
        loadTenders();
    }, [loadTenders]);

    // The next tender starts at everything that is left, which is the common
    // case, so the cashier only types an amount when splitting again.
    useEffect(() => {
        if (splitActive && outstanding !== null) {
            setTenderAmount(Number(outstanding));
        }
    }, [splitActive, outstanding]);

    // Load the service charge the order already carries. It is shown and counted
    // whatever the toggle says, so turning the feature off never silently drops
    // a charge the customer was already given.
    useEffect(() => {
        if (!orderId) return;
        fetch(`${BACKEND_URL}/api/orders/${orderId}`)
            .then(res => (res.ok ? res.json() : null))
            .then(data => {
                if (data && data.service_charge != null) {
                    setServiceCharge(Number(data.service_charge));
                }
            })
            .catch(err => console.error('Error loading service charge:', err));
    }, [orderId]);

    const checkApplicableSpecials = async () => {
        try {
            const response = await fetch(`${BACKEND_URL}/api/specials/check-applicable`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ order_items: orderItems })
            });

            if (response.ok) {
                const specials = await response.json();
                setApplicableSpecials(specials);
            }
        } catch (err) {
            console.error('Error checking specials:', err);
        } finally {
            setCheckingSpecials(false);
        }
    };

    // eslint-disable-next-line no-unused-vars
    const applySpecial = (special) => {
        if (special.special_type === 'combo_deal') {
            // Call backend to apply combo discount to order items
            const comboItemIds = (special.matching_items || []).map(i => i.id);
            const comboDiscount = special.combo_discount || (special.combo_total - special.combo_price);
            fetch(`${BACKEND_URL}/api/orders/${orderId}/apply-combo-discount`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    combo_special_id: special.id,
                    combo_discount: comboDiscount,
                    combo_item_ids: comboItemIds
                })
            })
            .then(res => res.json())
            .then(data => {
                if (data.success) {
                    setSelectedSpecial(special);
                    setSpecialDiscount(comboDiscount);
                } else {
                    alert(t('error') + ': ' + (data.error || 'Failed to apply combo discount'));
                }
            })
            .catch(err => {
                console.error('Error applying combo discount:', err);
                alert(t('error') + ': ' + err.message);
            });
        } else if (["buy_x_get_y_free", "percentage_discount", "fixed_discount"].includes(special.special_type)) {
            // Call backend to apply non-combo special discount
            const matchingItemIds = (special.matching_items || []).map(i => i.id);
            fetch(`${BACKEND_URL}/api/orders/${orderId}/apply-special-discount`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    special_id: special.id,
                    special_type: special.special_type,
                    discount_value: special.discount_value,
                    matching_item_ids: matchingItemIds
                })
            })
            .then(res => res.json())
            .then(data => {
                if (data.success) {
                    setSelectedSpecial(special);
                    setSpecialDiscount(data.total_discount || 0);
                } else {
                    alert(t('error') + ': ' + (data.error || 'Failed to apply special discount'));
                }
            })
            .catch(err => {
                console.error('Error applying special discount:', err);
                alert(t('error') + ': ' + err.message);
            });
        }
    };

    // eslint-disable-next-line no-unused-vars
    const removeSpecial = () => {
        fetch(`${BACKEND_URL}/api/orders/${orderId}/clear-discounts`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' }
        })
        .then(res => res.json())
        .then(data => {
            if (data.success) {
                setSelectedSpecial(null);
                setSpecialDiscount(0);
            } else {
                alert(t('error') + ': ' + (data.error || 'Failed to clear discounts'));
            }
        })
        .catch(err => {
            console.error('Error clearing discounts:', err);
            alert(t('error') + ': ' + err.message);
        });
    };
    // When the component mounts or orderId/orderItems changes, fetch the order to check for special_id and OpenPay charge
    useEffect(() => {
        if (!orderId) return;
        fetch(`${BACKEND_URL}/api/orders/${orderId}`)
            .then(res => res.json())
            .then(data => {
                if (data.special_id && data.special_name) {
                    setSelectedSpecial({ id: data.special_id, name: data.special_name, description: data.special_description });
                } else {
                    setSelectedSpecial(null);
                }
                // Load existing OpenPay charge data if present. Where tenders are
                // recorded the ledger says what has been paid, so restoring the
                // charge id would show a card leg that is already banked as
                // though it were still waiting on the cashier.
                if (data.openpay_charge_id && !splitPaymentsEnabled) {
                    setCardPaymentResult({
                        success: true,
                        charge_id: data.openpay_charge_id,
                        authorization: data.openpay_authorization,
                        status: 'completed'
                    });
                    setPaymentTypeId(CARD_PAYMENT_TYPE_ID);
                }
            })
            .catch(() => setSelectedSpecial(null));
    }, [orderId, orderItems, splitPaymentsEnabled]);

    const handleProcessCardPayment = async (skipTipCheck = false) => {
        if (!cardNumber || !cardholderName || !expirationMonth || !expirationYear || !cvv) {
            setError(t('error') + ': All card fields are required');
            return;
        }

        // Prompt if no tip entered
        if (!skipTipCheck && Number(tipAmount) === 0) {
            setShowNoTipConfirm(true);
            return;
        }

        setShowNoTipConfirm(false);
        setProcessingCard(true);
        setError('');
        setCardPaymentResult(null);

        try {
            const response = await fetch(`${BACKEND_URL}/api/openpay/test-charge`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    card_number: cardNumber.replace(/\s/g, ''),
                    holder_name: cardholderName,
                    expiration_month: expirationMonth.padStart(2, '0'),
                    expiration_year: expirationYear.length === 4 ? expirationYear.slice(-2) : expirationYear,
                    cvv2: cvv,
                    // The charge is for this tender, which is the whole bill
                    // when it is the only one
                    amount: tenderTotal,
                    description: `Order #${orderId} - ${tableName || ''} ${orderName || ''}`.trim(),
                    order_id: orderId
                })
            });

            const data = await response.json();

            if (data.success) {
                setCardPaymentResult({
                    success: true,
                    charge_id: data.charge_id,
                    authorization: data.authorization,
                    status: data.status,
                    amount: data.amount,
                    tip: Number(tipAmount)
                });

                // Immediately save charge data to the order so it persists
                // even if the user closes the modal before clicking "Close Check"
                fetch(`${BACKEND_URL}/api/orders/${orderId}`)
                    .then(r => r.json())
                    .then(order => {
                        fetch(`${BACKEND_URL}/api/orders/${orderId}`, {
                            method: 'PUT',
                            headers: { 'Content-Type': 'application/json' },
                            body: JSON.stringify({
                                name: order.name,
                                table_id: order.table_id,
                                closed: order.closed,
                                openpay_charge_id: data.charge_id,
                                openpay_authorization: data.authorization
                            })
                        });
                    })
                    .catch(err => console.error('Error saving charge data:', err));
            } else {
                setCardPaymentResult({
                    success: false,
                    error: data.details || data.error || t('paymentFailed')
                });
            }
        } catch (err) {
            console.error('Card payment error:', err);
            setCardPaymentResult({
                success: false,
                error: err.message
            });
        } finally {
            setProcessingCard(false);
        }
    };

    // no_active_register and multiple_active_registers can now come back from
    // either call, because a drawer is required whenever a tender in this close
    // is cash and not only when the whole order is cash. Both answer them the
    // way the close always has, and the retry runs whichever call asked.
    const handleRegisterError = (status, errorData, action) => {
        if (status !== 400) return false;

        if (errorData.error === 'multiple_active_registers') {
            // Multiple registers - show selection dialog
            setAvailableRegisters(errorData.sessions);
            setPendingRegisterAction(action);
            setShowRegisterSelector(true);
            return true;
        }

        if (errorData.error === 'no_active_register') {
            // No active register - prompt user
            setError(t('noActiveRegisterError'));
            return true;
        }

        return false;
    };

    // Records one tender and leaves the check open for the rest of the bill.
    // The tender is banked the moment it settles, so a leg that fails later
    // cannot take this one down with it.
    const handleRecordTender = async (registerSessionId = null) => {
        if (!orderId) {
            setError(t('noOrderIdProvided'));
            return;
        }

        setRecordingTender(true);
        setError('');
        setTenderNotice('');

        try {
            const requestBody = {
                payment_type_id: paymentTypeId,
                amount: Number(tenderAmount || 0),
                // Cash transactions don't have tips
                tip: paymentTypeId === CASH_PAYMENT_TYPE_ID ? 0 : Number(tipAmount || 0),
                user_id: user?.user_id || user?.id || 1
            };

            if (registerSessionId) {
                requestBody.register_session_id = registerSessionId;
            }

            // Include OpenPay charge data if this leg was charged on a card
            if (cardPaymentResult?.success) {
                requestBody.openpay_charge_id = cardPaymentResult.charge_id;
                requestBody.openpay_authorization = cardPaymentResult.authorization;
            }

            const response = await fetch(`${BACKEND_URL}/api/orders/${orderId}/payments`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify(requestBody),
            });

            if (!response.ok) {
                const errorData = await response.json();
                if (handleRegisterError(response.status, errorData, 'tender')) {
                    return;
                }
                throw new Error(errorData.message || errorData.error || t('failedToCloseOrder'));
            }

            // Read the remaining balance back from the server rather than
            // subtracting here
            await loadTenders();
            setTenderNotice(t('paymentRecorded'));

            // A cash leg opens the drawer as it settles, the way a cash close does
            if (paymentTypeId === CASH_PAYMENT_TYPE_ID) {
                openCashDrawer().catch(() => {});
            }

            // Start the next tender clean
            setPaymentTypeId(null);
            setTipAmount(0);
            setCashReceived('');
            setCardPaymentResult(null);
        } catch (err) {
            console.error('Error recording payment:', err);
            setError(err.message);
        } finally {
            setRecordingTender(false);
        }
    };

    const handleCloseOrder = async (registerSessionId = null) => {
        if (!orderId) {
            setError(t('noOrderIdProvided'));
            return;
        }

        setLoading(true);
        setError('');

        try {
            // Get current order data first
            const getResponse = await fetch(`${BACKEND_URL}/api/orders/${orderId}`);
            if (!getResponse.ok) {
                throw new Error(t('failedToFetchCurrentOrderData'));
            }
            const currentOrder = await getResponse.json();

            // For zero-balance orders, use default payment type if none selected
            const effectivePaymentTypeId = isZeroBalance && !paymentTypeId ? 1 : paymentTypeId;

            // Build the request body
            const requestBody = {
                name: currentOrder.name,
                table_id: currentOrder.table_id,
                closed: 1, // Mark as closed
                // orders.tip is the whole order's tip, so the tips already
                // banked on earlier tenders are part of it. The backend gives
                // the last tender only the part they have not claimed.
                tip: splitActive ? recordedTipTotal + Number(tipAmount || 0) : Number(tipAmount),
                payment_type_id: effectivePaymentTypeId,
                heard_about_id: heardAboutId,
                close_user_id: user?.user_id || user?.id || 1 // Set the user who is closing the check
            };

            // Only sent where the tenant offers the feature. With it off the
            // backend ignores the field anyway and the stored charge stays.
            if (serviceChargeEnabled) {
                requestBody.service_charge = Number(serviceCharge || 0);
            }

            // Include OpenPay charge data if card payment was processed
            if (cardPaymentResult?.success) {
                requestBody.openpay_charge_id = cardPaymentResult.charge_id;
                requestBody.openpay_authorization = cardPaymentResult.authorization;
            }

            // If a register session was selected, include it in the request
            // Skip register session requirement for zero-balance orders
            if (registerSessionId && !isZeroBalance) {
                requestBody.register_session_id = registerSessionId;
            }

            // Close the order using the existing PUT endpoint
            const response = await fetch(`${BACKEND_URL}/api/orders/${orderId}`, {
                method: 'PUT',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify(requestBody),
            });

            if (!response.ok) {
                const errorData = await response.json();

                // Handle register session errors for cash payments
                if (handleRegisterError(response.status, errorData, 'close')) {
                    return;
                }

                if (response.status === 400 && errorData.children) {
                    // Backend found open children, show them to user
                    const childrenList = errorData.children.map(child => child.name).join(', ');
                    throw new Error(`${t('cannotCloseParentTab')}:\n${t('openChildrenTabs')}: ${childrenList}`);
                }

                throw new Error(errorData.error || errorData.message || t('failedToCloseOrder'));
            }

            const result = await response.json();
            console.log(t('orderClosedSuccessfully') + ':', result);

            // Auto-open cash drawer on cash payment
            if (effectivePaymentTypeId === CASH_PAYMENT_TYPE_ID) {
                openCashDrawer().catch(() => {});
            }

            // Notify parent component that order was closed
            onOrderClosed();
        } catch (err) {
            console.error(t('errorClosingOrder') + ':', err);
            setError(err.message);
            // The close failed but anything already tendered is still banked,
            // so show what the ledger holds rather than a screen that reads as
            // though the whole payment was lost.
            loadTenders();
        } finally {
            setLoading(false);
        }
    };

    const handleRegisterSelection = (sessionId) => {
        setSelectedRegisterId(sessionId);
        setShowRegisterSelector(false);
        const action = pendingRegisterAction;
        setPendingRegisterAction(null);
        // Retry whichever call asked for a register, with the one selected
        if (action === 'tender') {
            handleRecordTender(sessionId);
        } else {
            handleCloseOrder(sessionId);
        }
    };

    return (
        <div
            style={{
                position: 'fixed',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                backgroundColor: 'rgba(0, 0, 0, 0.5)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                zIndex: 1000
            }}
            onClick={onClose}
        >
            {/* Spinner animation for card processing */}
            <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
            <div
                style={{
                    backgroundColor: theme?.colors?.surface || '#fff',
                    borderRadius: theme?.borderRadius || 8,
                    padding: '2rem',
                    maxWidth: '500px',
                    width: '90%',
                    // A flex item is min-content wide by default, so without
                    // this the payment method row makes the modal wider than the
                    // phone and the amounts on the right are cut off
                    minWidth: 0,
                    boxSizing: 'border-box',
                    maxHeight: '80vh',
                    overflowY: 'auto',
                    boxShadow: '0 10px 25px rgba(0, 0, 0, 0.2)'
                }}
                onClick={(e) => e.stopPropagation()}
            >
                <h2 style={{
                    color: theme?.colors?.primary || '#1976d2',
                    marginTop: 0,
                    textAlign: 'center'
                }}>
                    {t('closeCheck')}
                </h2>

                {/* Order Summary */}
                <div style={{
                    marginBottom: '1.5rem',
                    padding: '1rem',
                    backgroundColor: theme?.colors?.background || '#f5f5f5',
                    borderRadius: theme?.borderRadius || 6,
                    border: '1px solid #ddd'
                }}>
                    <h3 style={{ margin: '0 0 0.5rem 0', color: theme?.colors?.text }}>{t('orderSummary')}</h3>
                    <div style={{ fontSize: '0.9rem', color: theme?.colors?.textSecondary }}>
                        {tableName && <div>{t('table')}: <strong>{tableName}</strong></div>}
                        {orderName && <div>{t('name')}: <strong>{orderName}</strong></div>}
                        <div>{t('items')}: <strong>{orderItems.length}</strong></div>
                        <div>{t('subtotal')}: <strong>${subtotalBeforeDiscountAndIVA.toFixed(2)}</strong></div>
                        {computedDiscount > 0 && (
                            <div style={{ color: theme?.colors?.success || '#4caf50' }}>
                                {t('discount')}: <strong>-${computedDiscount.toFixed(2)}</strong>
                            </div>
                        )}
                        <div>{t('iva')} ({formatTaxRate(taxRate)}%): <strong>${ivaAmount.toFixed(2)}</strong></div>
                        {Number(serviceCharge) > 0 && (
                            <div>{t('serviceCharge')}: <strong>${Number(serviceCharge).toFixed(2)}</strong></div>
                        )}
                        <div>{t('total')}: <strong>${totalPrice.toFixed(2)}</strong></div>
                    </div>
                </div>

                {/* Specials Section */}
                {/* Applied Special (read-only) */}
                {selectedSpecial && (
                    <div style={{
                        marginBottom: '1.5rem',
                        padding: '1rem',
                        backgroundColor: '#e8f5e9',
                        borderRadius: theme?.borderRadius || 6,
                        border: '2px solid #4caf50'
                    }}>
                        <h3 style={{
                            margin: '0 0 0.75rem 0',
                            color: '#2e7d32',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '0.5rem'
                        }}>
                            🎁 {t('specialApplied')}
                        </h3>
                        <div style={{
                            padding: '0.75rem',
                            backgroundColor: '#fff',
                            borderRadius: theme?.borderRadius || 4,
                            marginBottom: '0.5rem',
                            border: '2px solid #4caf50'
                        }}>
                            <div style={{ fontWeight: 'bold', color: '#2e7d32', marginBottom: '0.25rem' }}>
                                {selectedSpecial.name}
                            </div>
                            {selectedSpecial.description && (
                                <div style={{ fontSize: '0.85rem', color: '#666', marginBottom: '0.5rem' }}>
                                    {selectedSpecial.description}
                                </div>
                            )}
                            <div style={{ fontSize: '0.9rem', color: '#2e7d32', fontWeight: 'bold' }}>
                                {t('discount')}: -${computedDiscount.toFixed(2)}
                            </div>
                        </div>
                    </div>
                )}

                {/* Tenders already recorded against this check */}
                {splitActive && recordedTenders.length > 0 && (
                    <div style={{
                        marginBottom: '1.5rem',
                        padding: '1rem',
                        backgroundColor: theme?.colors?.background || '#f5f5f5',
                        borderRadius: theme?.borderRadius || 6,
                        border: `2px solid ${theme?.colors?.success || '#4caf50'}`
                    }}>
                        <h3 style={{ margin: '0 0 0.5rem 0', color: theme?.colors?.text }}>
                            {t('paymentsTaken')}
                        </h3>
                        {recordedTenders.map(tender => (
                            <div
                                key={tender.id}
                                style={{
                                    fontSize: '0.9rem',
                                    color: theme?.colors?.textSecondary || '#666',
                                    marginBottom: '0.25rem'
                                }}
                            >
                                <div style={{ display: 'flex', justifyContent: 'space-between', gap: '0.5rem' }}>
                                    <span style={{ minWidth: 0 }}>{t(paymentMethodKey(tender.payment_type_id))}</span>
                                    <span><strong>${Number(tender.amount).toFixed(2)}</strong></span>
                                </div>
                                {Number(tender.tip) > 0 && (
                                    <div style={{ display: 'flex', justifyContent: 'space-between', gap: '0.5rem', fontSize: '0.85rem' }}>
                                        <span style={{ minWidth: 0 }}>{t('tip')}</span>
                                        <span>${Number(tender.tip).toFixed(2)}</span>
                                    </div>
                                )}
                            </div>
                        ))}
                        <div style={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            gap: '0.5rem',
                            marginTop: '0.5rem',
                            paddingTop: '0.5rem',
                            borderTop: `1px solid ${theme?.colors?.border || '#ccc'}`,
                            fontWeight: 'bold',
                            color: theme?.colors?.text
                        }}>
                            <span>{t('remainingBalance')}</span>
                            <span>${(outstandingValue || 0).toFixed(2)}</span>
                        </div>
                    </div>
                )}

                {/* Payment Method as buttons - Optional for zero-balance orders */}
                {!isZeroBalance && (
                    <div style={{ marginBottom: '1.5rem' }}>
                        <label style={{
                            display: 'block',
                            marginBottom: '0.5rem',
                            fontWeight: 'bold',
                            color: theme?.colors?.text
                        }}>
                            {t('paymentMethod')}:
                        </label>
                        <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
                            {PAYMENT_METHODS.map(method => {
                                const isSelected = paymentTypeId === method.id;
                                return (
                                    <button
                                        key={method.id}
                                        type="button"
                                        onClick={() => {
                                            setPaymentTypeId(method.id);
                                            // Cash transactions don't have tips
                                            if (method.id === CASH_PAYMENT_TYPE_ID) setTipAmount(0);
                                        }}
                                        style={{
                                            padding: '0.6rem 1.2rem',
                                            borderRadius: theme?.borderRadius || 4,
                                            border: isSelected ? `2px solid ${theme?.colors?.primary || '#1976d2'}` : `1px solid ${theme?.colors?.secondary || '#bbb'}`,
                                            backgroundColor: isSelected ? (theme?.colors?.primary || '#1976d2') : (theme?.colors?.surface || '#fff'),
                                            color: isSelected ? (theme?.colors?.surface || '#fff') : (theme?.colors?.text || '#222'),
                                            fontWeight: isSelected ? 'bold' : 'normal',
                                            fontSize: '1rem',
                                            cursor: 'pointer',
                                            outline: 'none',
                                            minWidth: 120,
                                            boxShadow: isSelected ? `0 0 0 2px ${(theme?.colors?.primary || '#1976d2')}33` : 'none',
                                            transition: 'all 0.15s'
                                        }}
                                    >
                                        {t(method.key)}
                                    </button>
                                );
                            })}
                        </div>
                    </div>
                )}

                {/* Zero Balance Notice */}
                {isZeroBalance && (
                    <div style={{
                        marginBottom: '1.5rem',
                        padding: '1rem',
                        backgroundColor: '#e8f5e9',
                        borderRadius: theme?.borderRadius || 6,
                        border: '2px solid #4caf50',
                        textAlign: 'center'
                    }}>
                        <div style={{
                            fontSize: '1.1rem',
                            fontWeight: 'bold',
                            color: '#2e7d32',
                            marginBottom: '0.5rem'
                        }}>
                            {t('complimentaryOrder')}
                        </div>
                        <div style={{
                            fontSize: '0.9rem',
                            color: '#666'
                        }}>
                            {t('noPaymentRequired')}
                        </div>
                    </div>
                )}

                {/* Service Charge - only where the tenant has the feature on */}
                {serviceChargeEnabled && (
                    <div style={{ marginBottom: '1.5rem' }}>
                        <label style={{
                            display: 'block',
                            marginBottom: '0.5rem',
                            fontWeight: 'bold',
                            color: theme?.colors?.text
                        }}>
                            {t('serviceCharge')}:
                        </label>
                        <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
                            <KeyboardAwareNumberInput
                                value={serviceCharge}
                                onChange={(value) => setServiceCharge(value)}
                                options={{ allowDecimals: true, min: 0 }}
                                placeholder="0.00"
                                style={{
                                    padding: '0.5rem',
                                    fontSize: '1rem',
                                    flex: 1
                                }}
                            />
                            {[10, 15].map(pct => (
                                <button
                                    key={pct}
                                    type="button"
                                    onClick={() => setServiceCharge(Math.round(totalPrice * pct) / 100)}
                                    style={{
                                        padding: '0.5rem 0.75rem',
                                        fontSize: '0.85rem',
                                        fontWeight: 'bold',
                                        border: `1px solid ${theme?.colors?.border || '#ccc'}`,
                                        borderRadius: theme?.borderRadius || 4,
                                        backgroundColor: Number(serviceCharge) === Math.round(totalPrice * pct) / 100 && Number(serviceCharge) > 0
                                            ? (theme?.colors?.primary || '#1976d2')
                                            : (theme?.colors?.surface || '#fff'),
                                        color: Number(serviceCharge) === Math.round(totalPrice * pct) / 100 && Number(serviceCharge) > 0
                                            ? (theme?.colors?.textLight || '#fff')
                                            : (theme?.colors?.text || '#000'),
                                        cursor: 'pointer',
                                        whiteSpace: 'nowrap'
                                    }}
                                >
                                    {pct}%
                                </button>
                            ))}
                        </div>
                        <p style={{
                            margin: '0.5rem 0 0 0',
                            fontSize: '0.85rem',
                            color: theme?.colors?.textSecondary || '#666'
                        }}>
                            {t('serviceChargeInfo')}
                        </p>
                    </div>
                )}

                {/* Amount this tender settles - only where splitting is offered */}
                {splitActive && (
                    <div style={{ marginBottom: '1.5rem' }}>
                        <label style={{
                            display: 'block',
                            marginBottom: '0.5rem',
                            fontWeight: 'bold',
                            color: theme?.colors?.text
                        }}>
                            {t('amount')}:
                        </label>
                        <KeyboardAwareNumberInput
                            value={tenderAmount}
                            onChange={(value) => setTenderAmount(value)}
                            options={{ allowDecimals: true, min: 0 }}
                            placeholder="0.00"
                            style={{
                                padding: '0.5rem',
                                fontSize: '1rem'
                            }}
                        />
                        <p style={{
                            margin: '0.5rem 0 0 0',
                            fontSize: '0.85rem',
                            color: theme?.colors?.textSecondary || '#666'
                        }}>
                            {t('partialPaymentInfo')}
                        </p>
                    </div>
                )}

                {/* Tip Amount - hidden for cash payments */}
                {paymentTypeId !== CASH_PAYMENT_TYPE_ID && <div style={{ marginBottom: '1.5rem' }}>
                    <label style={{
                        display: 'block',
                        marginBottom: '0.5rem',
                        fontWeight: 'bold',
                        color: theme?.colors?.text
                    }}>
                        {t('tipAmount')}:
                    </label>
                    <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
                        <KeyboardAwareNumberInput
                            value={tipAmount}
                            onChange={(value) => setTipAmount(value)}
                            options={{ allowDecimals: true, min: 0 }}
                            placeholder="0.00"
                            style={{
                                padding: '0.5rem',
                                fontSize: '1rem',
                                flex: 1
                            }}
                        />
                        {[10, 15, 20].map(pct => {
                            // A tip is a percentage of what this tender settles,
                            // which is the whole bill when it is the only one
                            const tipBase = splitActive ? Number(tenderAmount || 0) : totalPrice;
                            const pctTip = Math.round(tipBase * pct) / 100;
                            const isSelected = Number(tipAmount) === pctTip && Number(tipAmount) > 0;
                            return (
                                <button
                                    key={pct}
                                    type="button"
                                    onClick={() => setTipAmount(pctTip)}
                                    style={{
                                        padding: '0.5rem 0.75rem',
                                        fontSize: '0.85rem',
                                        fontWeight: 'bold',
                                        border: `1px solid ${theme?.colors?.border || '#ccc'}`,
                                        borderRadius: theme?.borderRadius || 4,
                                        backgroundColor: isSelected
                                            ? (theme?.colors?.primary || '#1976d2')
                                            : (theme?.colors?.surface || '#fff'),
                                        color: isSelected
                                            ? '#fff'
                                            : (theme?.colors?.text || '#000'),
                                        cursor: 'pointer',
                                        whiteSpace: 'nowrap'
                                    }}
                                >
                                    {pct}%
                                </button>
                            );
                        })}
                    </div>
                </div>}

                {/* Cash Received (only show when Cash is selected) */}
                {paymentTypeId === CASH_PAYMENT_TYPE_ID && (
                    <div style={{ marginBottom: '1.5rem' }}>
                        <label style={{
                            display: 'block',
                            marginBottom: '0.5rem',
                            fontWeight: 'bold',
                            color: theme?.colors?.text
                        }}>
                            {t('cashReceived')}:
                        </label>
                        <KeyboardAwareNumberInput
                            value={cashReceived}
                            onChange={(value) => setCashReceived(value)}
                            options={{ allowDecimals: true, min: 0 }}
                            placeholder="0.00"
                            style={{
                                padding: '0.5rem',
                                fontSize: '1rem'
                            }}
                        />

                        {/* Change Display */}
                        {cashReceived && Number(cashReceived) > 0 && (
                            <div style={{
                                marginTop: '1rem',
                                padding: '1rem',
                                backgroundColor: cashChange >= 0 ? '#e8f5e9' : '#ffebee',
                                borderRadius: theme?.borderRadius || 6,
                                border: `2px solid ${cashChange >= 0 ? '#4caf50' : '#f44336'}`,
                                textAlign: 'center'
                            }}>
                                {/* Calculation Breakdown */}
                                <div style={{
                                    fontSize: '0.85rem',
                                    color: theme?.colors?.textSecondary,
                                    marginBottom: '0.75rem',
                                    lineHeight: '1.5'
                                }}>
                                    {t('cashReceived')}: <strong>${Number(cashReceived).toFixed(2)}</strong><br />
                                    {splitActive ? (
                                        <>{t('amount')}: <strong>-${Number(tenderAmount || 0).toFixed(2)}</strong><br /></>
                                    ) : (
                                        <>
                                            {t('total')}: <strong>-${totalPrice.toFixed(2)}</strong><br />
                                            {Number(serviceCharge) > 0 && <>{t('serviceCharge')}: <strong>-${Number(serviceCharge).toFixed(2)}</strong><br /></>}
                                        </>
                                    )}
                                    {tipAmount > 0 && <>{t('tip')}: <strong>-${Number(tipAmount).toFixed(2)}</strong><br /></>}
                                </div>

                                <div style={{
                                    fontSize: '0.9rem',
                                    color: theme?.colors?.textSecondary,
                                    marginBottom: '0.5rem',
                                    fontWeight: 'bold'
                                }}>
                                    {t('change')}:
                                </div>
                                <div style={{
                                    fontSize: '1.5rem',
                                    fontWeight: 'bold',
                                    color: cashChange >= 0 ? '#2e7d32' : '#c62828'
                                }}>
                                    ${Math.abs(cashChange).toFixed(2)}
                                </div>

                                {tipAmount > 0 && (
                                    <>
                                        <div style={{
                                            fontSize: '0.9rem',
                                            color: theme?.colors?.textSecondary,
                                            marginTop: '1rem',
                                            marginBottom: '0.5rem',
                                            fontWeight: 'bold'
                                        }}>
                                            {t('tip')}:
                                        </div>
                                        <div style={{
                                            fontSize: '1.5rem',
                                            fontWeight: 'bold',
                                            color: '#2e7d32'
                                        }}>
                                            ${Number(tipAmount).toFixed(2)}
                                        </div>
                                    </>
                                )}

                                {cashChange < 0 && (
                                    <div style={{
                                        fontSize: '0.85rem',
                                        color: '#c62828',
                                        marginTop: '0.5rem'
                                    }}>
                                        {t('insufficientCash')}
                                    </div>
                                )}
                            </div>
                        )}
                    </div>
                )}

                {/* OpenPay Card Payment Form - only on dev/local */}
                {openPayEnabled && paymentTypeId === CARD_PAYMENT_TYPE_ID && !isZeroBalance && (
                    <div style={{
                        marginBottom: '1.5rem',
                        padding: '1rem',
                        backgroundColor: theme?.colors?.background || '#f5f5f5',
                        borderRadius: theme?.borderRadius || 6,
                        border: `2px solid ${theme?.colors?.primary || '#1976d2'}`
                    }}>
                        <div style={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between',
                            marginBottom: '1rem'
                        }}>
                            <h3 style={{ margin: 0, color: theme?.colors?.text }}>
                                {t('processCardPayment')}
                            </h3>
                            <span style={{
                                fontSize: '0.75rem',
                                padding: '0.2rem 0.5rem',
                                backgroundColor: '#ff9800',
                                color: '#fff',
                                borderRadius: 4,
                                fontWeight: 'bold'
                            }}>
                                {t('testModeOnly')}
                            </span>
                        </div>

                        {/* Card payment success */}
                        {cardPaymentResult?.success && (
                            <div style={{
                                padding: '1rem',
                                backgroundColor: '#e8f5e9',
                                borderRadius: theme?.borderRadius || 6,
                                border: '2px solid #4caf50',
                                textAlign: 'center'
                            }}>
                                <div style={{ fontSize: '1.1rem', fontWeight: 'bold', color: '#2e7d32', marginBottom: '0.5rem' }}>
                                    {t('paymentApproved')}
                                </div>
                                <div style={{ fontSize: '0.95rem', color: '#333', marginBottom: '0.25rem' }}>
                                    {t('charged')}: <strong>${(cardPaymentResult.amount || totalPrice).toFixed(2)}</strong>
                                    {cardPaymentResult.tip > 0 && (
                                        <span style={{ fontSize: '0.85rem', color: '#555' }}>
                                            {' '}({t('subtotal')}: ${(cardPaymentResult.amount - cardPaymentResult.tip).toFixed(2)} + {t('tip')}: ${cardPaymentResult.tip.toFixed(2)})
                                        </span>
                                    )}
                                </div>
                                <div style={{ fontSize: '0.85rem', color: '#666' }}>
                                    {t('authorizationCode')}: <strong>{cardPaymentResult.authorization}</strong>
                                </div>
                                <div style={{ fontSize: '0.8rem', color: '#999', marginTop: '0.25rem' }}>
                                    ID: {cardPaymentResult.charge_id}
                                </div>
                            </div>
                        )}

                        {/* Card payment error */}
                        {cardPaymentResult && !cardPaymentResult.success && (
                            <div style={{
                                padding: '1rem',
                                backgroundColor: '#ffebee',
                                borderRadius: theme?.borderRadius || 6,
                                border: '2px solid #f44336',
                                textAlign: 'center',
                                marginBottom: '1rem'
                            }}>
                                <div style={{ fontSize: '1rem', fontWeight: 'bold', color: '#c62828', marginBottom: '0.25rem' }}>
                                    {t('paymentFailed')}
                                </div>
                                <div style={{ fontSize: '0.85rem', color: '#666' }}>
                                    {cardPaymentResult.error}
                                </div>
                            </div>
                        )}

                        {/* Card form - hide after successful payment */}
                        {!cardPaymentResult?.success && (
                            <>
                                <div style={{ marginBottom: '0.75rem' }}>
                                    <label style={{ display: 'block', marginBottom: '0.25rem', fontSize: '0.85rem', color: theme?.colors?.textSecondary }}>
                                        {t('cardNumber')}
                                    </label>
                                    <input
                                        type="text"
                                        value={cardNumber}
                                        onChange={(e) => {
                                            const digits = e.target.value.replace(/\D/g, '').slice(0, 16);
                                            const formatted = digits.replace(/(\d{4})(?=\d)/g, '$1 ');
                                            setCardNumber(formatted);
                                        }}
                                        placeholder="4111 1111 1111 1111"
                                        maxLength={19}
                                        style={{
                                            width: '100%',
                                            padding: '0.5rem',
                                            fontSize: '1rem',
                                            borderRadius: theme?.borderRadius || 4,
                                            border: `1px solid ${theme?.colors?.border || '#ddd'}`,
                                            backgroundColor: theme?.colors?.surface || '#fff',
                                            color: theme?.colors?.text || '#000',
                                            boxSizing: 'border-box'
                                        }}
                                    />
                                </div>

                                <div style={{ marginBottom: '0.75rem' }}>
                                    <label style={{ display: 'block', marginBottom: '0.25rem', fontSize: '0.85rem', color: theme?.colors?.textSecondary }}>
                                        {t('cardholderName')}
                                    </label>
                                    <input
                                        type="text"
                                        value={cardholderName}
                                        onChange={(e) => setCardholderName(e.target.value)}
                                        placeholder="John Doe"
                                        style={{
                                            width: '100%',
                                            padding: '0.5rem',
                                            fontSize: '1rem',
                                            borderRadius: theme?.borderRadius || 4,
                                            border: `1px solid ${theme?.colors?.border || '#ddd'}`,
                                            backgroundColor: theme?.colors?.surface || '#fff',
                                            color: theme?.colors?.text || '#000',
                                            boxSizing: 'border-box'
                                        }}
                                    />
                                </div>

                                <div style={{ display: 'flex', gap: '0.5rem', marginBottom: '0.75rem' }}>
                                    <div style={{ flex: 1 }}>
                                        <label style={{ display: 'block', marginBottom: '0.25rem', fontSize: '0.85rem', color: theme?.colors?.textSecondary }}>
                                            {t('expirationMonth')}
                                        </label>
                                        <input
                                            type="text"
                                            value={expirationMonth}
                                            onChange={(e) => setExpirationMonth(e.target.value.replace(/\D/g, '').slice(0, 2))}
                                            placeholder="MM"
                                            maxLength={2}
                                            style={{
                                                width: '100%',
                                                padding: '0.5rem',
                                                fontSize: '1rem',
                                                borderRadius: theme?.borderRadius || 4,
                                                border: `1px solid ${theme?.colors?.border || '#ddd'}`,
                                                backgroundColor: theme?.colors?.surface || '#fff',
                                                color: theme?.colors?.text || '#000',
                                                boxSizing: 'border-box'
                                            }}
                                        />
                                    </div>
                                    <div style={{ flex: 1 }}>
                                        <label style={{ display: 'block', marginBottom: '0.25rem', fontSize: '0.85rem', color: theme?.colors?.textSecondary }}>
                                            {t('expirationYear')}
                                        </label>
                                        <input
                                            type="text"
                                            value={expirationYear}
                                            onChange={(e) => setExpirationYear(e.target.value.replace(/\D/g, '').slice(0, 2))}
                                            placeholder="YY"
                                            maxLength={2}
                                            style={{
                                                width: '100%',
                                                padding: '0.5rem',
                                                fontSize: '1rem',
                                                borderRadius: theme?.borderRadius || 4,
                                                border: `1px solid ${theme?.colors?.border || '#ddd'}`,
                                                backgroundColor: theme?.colors?.surface || '#fff',
                                                color: theme?.colors?.text || '#000',
                                                boxSizing: 'border-box'
                                            }}
                                        />
                                    </div>
                                    <div style={{ flex: 1 }}>
                                        <label style={{ display: 'block', marginBottom: '0.25rem', fontSize: '0.85rem', color: theme?.colors?.textSecondary }}>
                                            {t('cvv')}
                                        </label>
                                        <input
                                            type="text"
                                            value={cvv}
                                            onChange={(e) => setCvv(e.target.value.replace(/\D/g, '').slice(0, 4))}
                                            placeholder="123"
                                            maxLength={4}
                                            style={{
                                                width: '100%',
                                                padding: '0.5rem',
                                                fontSize: '1rem',
                                                borderRadius: theme?.borderRadius || 4,
                                                border: `1px solid ${theme?.colors?.border || '#ddd'}`,
                                                backgroundColor: theme?.colors?.surface || '#fff',
                                                color: theme?.colors?.text || '#000',
                                                boxSizing: 'border-box'
                                            }}
                                        />
                                    </div>
                                </div>

                                <button
                                    onClick={() => handleProcessCardPayment()}
                                    disabled={processingCard || !cardNumber || !cardholderName || !expirationMonth || !expirationYear || !cvv}
                                    style={{
                                        width: '100%',
                                        padding: '0.75rem',
                                        fontSize: '1rem',
                                        fontWeight: 'bold',
                                        borderRadius: theme?.borderRadius || 4,
                                        border: 'none',
                                        backgroundColor: (processingCard || !cardNumber || !cardholderName || !expirationMonth || !expirationYear || !cvv)
                                            ? '#ccc' : (theme?.colors?.primary || '#1976d2'),
                                        color: '#fff',
                                        cursor: (processingCard || !cardNumber) ? 'not-allowed' : 'pointer'
                                    }}
                                >
                                    {processingCard ? (
                                        <span style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '0.5rem' }}>
                                            <span style={{
                                                display: 'inline-block',
                                                width: '1rem',
                                                height: '1rem',
                                                border: '2px solid rgba(255,255,255,0.3)',
                                                borderTopColor: '#fff',
                                                borderRadius: '50%',
                                                animation: 'spin 0.8s linear infinite'
                                            }} />
                                            {t('processingPayment')}...
                                        </span>
                                    ) : t('processCardPayment')}
                                </button>

                                {/* No tip confirmation */}
                                {showNoTipConfirm && (
                                    <div style={{
                                        marginTop: '0.75rem',
                                        padding: '0.75rem',
                                        backgroundColor: '#fff3e0',
                                        border: '1px solid #ff9800',
                                        borderRadius: theme?.borderRadius || 4,
                                        textAlign: 'center'
                                    }}>
                                        <div style={{ fontSize: '0.9rem', color: '#e65100', marginBottom: '0.5rem', fontWeight: 'bold' }}>
                                            {t('noTipConfirm')}
                                        </div>
                                        <div style={{ display: 'flex', gap: '0.5rem' }}>
                                            <button
                                                onClick={() => handleProcessCardPayment(true)}
                                                style={{
                                                    flex: 1,
                                                    padding: '0.5rem',
                                                    fontSize: '0.9rem',
                                                    border: 'none',
                                                    borderRadius: theme?.borderRadius || 4,
                                                    backgroundColor: '#757575',
                                                    color: '#fff',
                                                    cursor: 'pointer'
                                                }}
                                            >
                                                {t('continueWithoutTip')}
                                            </button>
                                            <button
                                                onClick={() => setShowNoTipConfirm(false)}
                                                style={{
                                                    flex: 1,
                                                    padding: '0.5rem',
                                                    fontSize: '0.9rem',
                                                    border: 'none',
                                                    borderRadius: theme?.borderRadius || 4,
                                                    backgroundColor: theme?.colors?.primary || '#1976d2',
                                                    color: '#fff',
                                                    cursor: 'pointer'
                                                }}
                                            >
                                                {t('addTip')}
                                            </button>
                                        </div>
                                    </div>
                                )}

                                {cardPaymentResult && !cardPaymentResult.success && (
                                    <button
                                        onClick={() => setCardPaymentResult(null)}
                                        style={{
                                            width: '100%',
                                            padding: '0.5rem',
                                            marginTop: '0.5rem',
                                            fontSize: '0.9rem',
                                            borderRadius: theme?.borderRadius || 4,
                                            border: `1px solid ${theme?.colors?.border || '#ddd'}`,
                                            backgroundColor: theme?.colors?.surface || '#fff',
                                            color: theme?.colors?.text || '#000',
                                            cursor: 'pointer'
                                        }}
                                    >
                                        {t('retryPayment')}
                                    </button>
                                )}
                            </>
                        )}
                    </div>
                )}

                {/* How did you hear about us */}
                <div style={{ marginBottom: '1.5rem' }}>
                    <label style={{
                        display: 'block',
                        marginBottom: '0.5rem',
                        fontWeight: 'bold',
                        color: theme?.colors?.text
                    }}>
                        {t('howDidYouHearAboutUs')}?
                    </label>
                    <select
                        value={heardAboutId}
                        onChange={(e) => setHeardAboutId(Number(e.target.value))}
                        style={{
                            width: '100%',
                            padding: '0.5rem',
                            borderRadius: theme?.borderRadius || 4,
                            border: '1px solid #ddd',
                            fontSize: '1rem'
                        }}
                    >
                        <option value={1}>{t('otherNotSpecified')}</option>
                        <option value={2}>Instagram</option>
                        <option value={3}>Google</option>
                        <option value={4}>{t('friend')}</option>
                        <option value={5}>{t('groupMeetup')}</option>
                    </select>
                </div>

                {/* Final Total */}
                <div style={{
                    marginBottom: '1.5rem',
                    padding: '1rem',
                    backgroundColor: theme?.colors?.primary || '#1976d2',
                    color: '#fff',
                    borderRadius: theme?.borderRadius || 6,
                    textAlign: 'center'
                }}>
                    <div style={{ fontSize: '0.9rem', marginBottom: '0.5rem', opacity: 0.9 }}>
                        {t('subtotal')}: ${subtotalBeforeDiscountAndIVA.toFixed(2)}<br />
                        {computedDiscount > 0 && <>{t('discount')}: -${computedDiscount.toFixed(2)}<br /></>}
                        {t('iva')} ({formatTaxRate(taxRate)}%): +${ivaAmount.toFixed(2)}<br />
                        {Number(serviceCharge) > 0 && <>{t('serviceCharge')}: +${Number(serviceCharge).toFixed(2)}<br /></>}
                        {tipAmount > 0 && <>{t('tip')}: +${Number(tipAmount).toFixed(2)}<br /></>}
                    </div>
                    <h3 style={{ margin: '0', fontSize: '1.2rem' }}>
                        {t('finalTotal')}: ${finalTotal.toFixed(2)}
                    </h3>
                    {splitActive && recordedTenders.length > 0 && (
                        <div style={{ fontSize: '0.85rem', marginTop: '0.5rem', opacity: 0.9, borderTop: '1px solid rgba(255,255,255,0.3)', paddingTop: '0.5rem' }}>
                            {t('paid')}: ${recordedAmountTotal.toFixed(2)}
                            <div style={{ marginTop: '0.25rem', fontWeight: 'bold' }}>
                                {t('remainingBalance')}: ${(outstandingValue || 0).toFixed(2)}
                            </div>
                        </div>
                    )}
                    {cardPaymentResult?.success && (
                        <div style={{ fontSize: '0.85rem', marginTop: '0.5rem', opacity: 0.9, borderTop: '1px solid rgba(255,255,255,0.3)', paddingTop: '0.5rem' }}>
                            {t('cardCharge')}: ${(cardPaymentResult.amount || totalPrice).toFixed(2)}
                            {cardPaymentResult.tip > 0 && (
                                <span> ({t('tip')} ${cardPaymentResult.tip.toFixed(2)} {t('included')})</span>
                            )}
                            {Number(tipAmount) > (cardPaymentResult.tip || 0) && (
                                <div style={{ marginTop: '0.25rem', fontWeight: 'bold' }}>
                                    {t('remainingBalance')}: ${(finalTotal - (cardPaymentResult.amount || totalPrice)).toFixed(2)}
                                </div>
                            )}
                        </div>
                    )}
                </div>

                {/* Payment recorded confirmation */}
                {tenderNotice && (
                    <div style={{
                        marginBottom: '1rem',
                        padding: '0.5rem',
                        backgroundColor: '#e8f5e9',
                        color: '#2e7d32',
                        borderRadius: theme?.borderRadius || 4,
                        fontSize: '0.9rem',
                        textAlign: 'center'
                    }}>
                        {tenderNotice}
                    </div>
                )}

                {/* Error Message */}
                {error && (
                    <div style={{
                        marginBottom: '1rem',
                        padding: '0.5rem',
                        backgroundColor: '#ffebee',
                        color: '#c62828',
                        borderRadius: theme?.borderRadius || 4,
                        fontSize: '0.9rem',
                        textAlign: 'center'
                    }}>
                        {error}
                        {/* A leg that already settled is banked, so a failure
                            further along is not a failure of the whole close */}
                        {recordedTenders.length > 0 && (
                            <div style={{ marginTop: '0.25rem', fontWeight: 'bold' }}>
                                {t('earlierPaymentsKept')}
                            </div>
                        )}
                    </div>
                )}

                {/* Action Buttons */}
                <div style={{
                    display: 'flex',
                    gap: '1rem',
                    justifyContent: 'flex-end'
                }}>
                    <button
                        onClick={onClose}
                        disabled={busy}
                        style={{
                            backgroundColor: theme?.colors?.secondary || '#666',
                            color: theme?.colors?.surface || '#fff',
                            border: 'none',
                            borderRadius: theme?.borderRadius || 4,
                            padding: '0.75rem 1.5rem',
                            cursor: busy ? 'not-allowed' : 'pointer',
                            fontSize: '1rem',
                            fontWeight: 'bold'
                        }}
                    >
                        {t('cancel')}
                    </button>
                    <button
                        onClick={() => (recordsTender ? handleRecordTender() : handleCloseOrder())}
                        disabled={actionDisabled}
                        style={{
                            backgroundColor: actionDisabled ? '#ccc' : '#d32f2f',
                            color: '#fff',
                            border: 'none',
                            borderRadius: theme?.borderRadius || 4,
                            padding: '0.75rem 1.5rem',
                            cursor: actionDisabled ? 'not-allowed' : 'pointer',
                            fontSize: '1rem',
                            fontWeight: 'bold'
                        }}
                    >
                        {recordingTender ? t('saving') : loading ? t('closing') : recordsTender ? t('recordPayment') : t('closeCheck')}
                    </button>
                </div>
            </div>

            {/* Register Session Selector Modal */}
            {showRegisterSelector && (
                <div style={{
                    position: 'fixed',
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    backgroundColor: 'rgba(0, 0, 0, 0.7)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    zIndex: 1100
                }}>
                    <div style={{
                        backgroundColor: theme?.colors?.surface || '#fff',
                        borderRadius: theme?.borderRadius || 8,
                        padding: '2rem',
                        maxWidth: '400px',
                        width: '90%',
                        boxShadow: '0 10px 25px rgba(0, 0, 0, 0.3)'
                    }}>
                        <h3 style={{
                            color: theme?.colors?.primary || '#1976d2',
                            marginTop: 0,
                            textAlign: 'center'
                        }}>
                            {t('selectRegisterSession')}
                        </h3>

                        <p style={{
                            color: theme?.colors?.textSecondary || '#666',
                            fontSize: '0.9rem',
                            textAlign: 'center',
                            marginBottom: '1.5rem'
                        }}>
                            {t('multipleRegistersActive')}
                        </p>

                        <div style={{
                            display: 'flex',
                            flexDirection: 'column',
                            gap: '0.75rem',
                            marginBottom: '1.5rem'
                        }}>
                            {availableRegisters.map(register => (
                                <button
                                    key={register.id}
                                    onClick={() => handleRegisterSelection(register.id)}
                                    style={{
                                        padding: '1rem',
                                        borderRadius: theme?.borderRadius || 6,
                                        border: `2px solid ${theme?.colors?.primary || '#1976d2'}`,
                                        backgroundColor: theme?.colors?.surface || '#fff',
                                        color: theme?.colors?.text || '#222',
                                        fontSize: '1rem',
                                        fontWeight: 'bold',
                                        cursor: 'pointer',
                                        textAlign: 'center',
                                        transition: 'all 0.2s'
                                    }}
                                    onMouseEnter={(e) => {
                                        e.currentTarget.style.backgroundColor = theme?.colors?.primary || '#1976d2';
                                        e.currentTarget.style.color = theme?.colors?.surface || '#fff';
                                    }}
                                    onMouseLeave={(e) => {
                                        e.currentTarget.style.backgroundColor = theme?.colors?.surface || '#fff';
                                        e.currentTarget.style.color = theme?.colors?.text || '#222';
                                    }}
                                >
                                    {t('register')} #{register.register_id}
                                </button>
                            ))}
                        </div>

                        <button
                            onClick={() => {
                                setShowRegisterSelector(false);
                                setAvailableRegisters([]);
                            }}
                            style={{
                                width: '100%',
                                padding: '0.75rem',
                                borderRadius: theme?.borderRadius || 4,
                                border: 'none',
                                backgroundColor: theme?.colors?.secondary || '#666',
                                color: theme?.colors?.surface || '#fff',
                                fontSize: '1rem',
                                fontWeight: 'bold',
                                cursor: 'pointer'
                            }}
                        >
                            {t('cancel')}
                        </button>
                    </div>
                </div>
            )}
        </div>
    );
}

export default OrderClose;